import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  Alert,
  AlertTitle,
  Stack,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  CloudOff as CloudOffIcon,
} from '@mui/icons-material';
import { useKioskAuth, KioskAuthErrorCode } from '../contexts/KioskAuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

interface ErrorPresentation {
  title: string;
  alertTitle: string;
  message: string;
  instructions: string;
  canRetry: boolean;
  offline?: boolean;
}

/**
 * Each failure gets its own advice. Telling staff to "start a new enrollment session" when the
 * backend is simply unreachable sends them in circles - a new token cannot fix a dead upstream.
 */
const ERROR_PRESENTATION: Record<KioskAuthErrorCode, ErrorPresentation> = {
  missingToken: {
    title: 'auth.sessionError',
    alertTitle: 'auth.accessDenied',
    message: 'auth.noSession',
    instructions: 'auth.instructionsError',
    canRetry: false,
  },
  invalidSession: {
    title: 'auth.sessionError',
    alertTitle: 'auth.accessDenied',
    message: 'auth.invalidSessionMessage',
    instructions: 'auth.instructionsError',
    canRetry: false,
  },
  sessionExpired: {
    title: 'auth.sessionExpired',
    alertTitle: 'auth.sessionExpired',
    message: 'auth.sessionExpiredMessage',
    instructions: 'auth.instructionsError',
    canRetry: false,
  },
  serverUnreachable: {
    title: 'auth.connectionError',
    alertTitle: 'auth.serverUnreachable',
    message: 'auth.serverUnreachableMessage',
    instructions: 'auth.instructionsServerUnreachable',
    canRetry: true,
    offline: true,
  },
  unknown: {
    title: 'auth.sessionError',
    alertTitle: 'auth.accessDenied',
    message: 'auth.unknownErrorMessage',
    instructions: 'auth.instructionsError',
    canRetry: true,
  },
};

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { t } = useTranslation();
  const { isLoading, isAuthenticated, error, errorCode, session, expiresAt, retry } = useKioskAuth();

  // Show loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 3,
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          {t('auth.verifying') || 'Verifying session...'}
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (error || !isAuthenticated) {
    const presentation = ERROR_PRESENTATION[errorCode ?? 'missingToken'];

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 3,
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 480,
            width: '100%',
          }}
        >
          {presentation.offline ? (
            <CloudOffIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          ) : (
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          )}

          <Typography variant="h5" gutterBottom fontWeight={600}>
            {t(presentation.title)}
          </Typography>

          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            <AlertTitle>{t(presentation.alertTitle)}</AlertTitle>
            {t(presentation.message)}
          </Alert>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t(presentation.instructions)}
          </Typography>

          <Stack spacing={1.5}>
            {presentation.canRetry && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={retry}
                size="large"
                fullWidth
              >
                {t('auth.retry')}
              </Button>
            )}

            <Button
              variant={presentation.canRetry ? 'outlined' : 'contained'}
              color="primary"
              startIcon={<CloseIcon />}
              onClick={() => window.close()}
              size="large"
              fullWidth
            >
              {t('auth.closeWindow')}
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Show session info bar
  const remainingMinutes = expiresAt ? Math.max(0, Math.floor((expiresAt - Date.now()) / 60000)) : 0;
  const isExpiringSoon = remainingMinutes <= 5;

  return (
    <>
      {/* Session info bar */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: isExpiringSoon ? 'warning.light' : 'success.light',
          color: isExpiringSoon ? 'warning.contrastText' : 'success.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {isExpiringSoon && <WarningIcon fontSize="small" />}
        <Typography variant="body2">
          {t('auth.sessionBy') || 'Session by'}: <strong>{session?.staffName}</strong>
          {' | '}
          {isExpiringSoon
            ? (t('auth.expiringSoon') || 'Session expiring soon!')
            : (t('auth.minutesRemaining', { count: remainingMinutes }) || `${remainingMinutes} min remaining`)}
        </Typography>
      </Box>

      {/* Protected content */}
      {children}
    </>
  );
}

export default ProtectedRoute;
